import React, { useEffect, useMemo, useState } from 'react';
import {
  Header,
  Pagination,
  Select,
  SelectProps,
  SpaceBetween,
  Table,
  TableProps,
  Toggle,
} from '@amzn/awsui-components-react-v3';
import { useCollection } from '@amzn/awsui-collection-hooks';
import { SlaExceptionRow, SlaVerdict } from '../../../interfaces/panel-data';
import { formatDateFromDate, formatTimeFromDate } from '../../../utils/date-helper';
import { formatDuration } from '../../../utils/format-duration';
import { Badge, BadgeVariant } from '../Badge';
import { VERDICT_LABELS } from './sla-verdict-labels';
import './SlaExceptionTable.scss';

const PAGE_SIZE = 20;

/** Stands in for every absent value in the table, so a blank cell is never a value in its own right. */
const ABSENT = '—';

/** The sentinel for "every group", kept out of the key namespace a stage or leg code could occupy. */
const ALL_GROUPS = '*';

/**
 * What the breaching-only filter keeps. Narrower than "is an exception": the list also carries stale and
 * unregistered rows, and neither is a budget overrun.
 */
const BREACHING_VERDICTS: SlaVerdict[] = ['BREACHED', 'BREACHING'];

/** The SPORC console's own execution view. External, so not an `AppRoutes` entry. */
const sporcExecutionUrl = (executionId: string): string =>
  `https://smt-sporc-v2.corp.amazon.com/execution?execution_id=${encodeURIComponent(executionId)}`;

/** `neutral` for UNMEASURED, because nothing was judged — see the same rule in `SlaPanel`. */
const VERDICT_VARIANTS: Record<SlaVerdict, BadgeVariant> = {
  WITHIN_SLA: 'success',
  AT_RISK: 'warning',
  BREACHING: 'error',
  BREACHED: 'error',
  // `error` rather than `warning`: an errored leg is not a milder version of a late one.
  FAILED: 'error',
  NOT_REGISTERED: 'warning',
  STALE: 'warning',
  UNMEASURED: 'neutral',
};

const duration = (milliseconds: number | null): string =>
  milliseconds === null ? ABSENT : formatDuration(milliseconds);

/**
 * Elapsed minus budget, which is negative on a row inside its budget — the table lists those now, so the
 * column has to report headroom rather than throw. The sign is stripped before `formatDuration`, which
 * rejects negatives so that an overrun computed the wrong way round surfaces as the defect it is.
 */
const margin = (milliseconds: number | null): string => {
  if (milliseconds === null) {
    return ABSENT;
  }
  return milliseconds < 0 ? `−${formatDuration(-milliseconds)}` : formatDuration(milliseconds);
};

const timestamp = (milliseconds: number): string => {
  const at = new Date(milliseconds);
  return `${formatDateFromDate(at)} ${formatTimeFromDate(at)}`;
};

/**
 * Outside the definition builder so the default sorting state can point at this exact object — collection
 * hooks match the sorting column by reference. Null sorts below every real margin rather than being skipped,
 * which puts rows with no budget last: they are listed for being unregistered or stale, not for overrunning.
 *
 * Null is tested for rather than substituted with a sentinel, because a row inside its budget has a negative
 * margin and any finite sentinel would rank above it — putting the rows with no margin at all in among the
 * healthy ones instead of beneath them.
 */
const OVER_BY_COLUMN: TableProps.ColumnDefinition<SlaExceptionRow> = {
  id: 'overBy',
  header: 'Over by',
  cell: (row) => margin(row.overByMs),
  sortingComparator: (a, b) => {
    if (a.overByMs === null) {
      return b.overByMs === null ? 0 : -1;
    }
    return b.overByMs === null ? 1 : a.overByMs - b.overByMs;
  },
};

/**
 * `groupNames` rather than the raw key, so no stage or leg code reaches the screen; `groupHeader` because one
 * tier's groups are stages and the other's are legs.
 */
const columnDefinitions = (
  showWebsite: boolean,
  groupNames: Record<string, string>,
  groupHeader: string
): Array<TableProps.ColumnDefinition<SlaExceptionRow>> => [
  {
    id: 'id',
    header: 'Record',
    // Plain text rather than a dead link where there is no execution to open: the id is still worth copying.
    cell: (row) =>
      row.sporcExecutionId === undefined ? (
        row.displayId
      ) : (
        <a
          className="ovp-sla-exceptions__record-link"
          href={sporcExecutionUrl(row.sporcExecutionId)}
          target="_blank"
          rel="noopener noreferrer"
        >
          {row.displayId}
        </a>
      ),
    sortingField: 'displayId',
  },
  ...(showWebsite
    ? [
        {
          id: 'website',
          header: 'Website',
          cell: (row: SlaExceptionRow) => row.website ?? ABSENT,
          sortingField: 'website',
        },
      ]
    : []),
  {
    id: 'group',
    header: groupHeader,
    cell: (row) => groupNames[row.groupKey] ?? row.groupKey,
    sortingField: 'groupKey',
  },
  { id: 'status', header: 'Status', cell: (row) => row.status ?? ABSENT, sortingField: 'status' },
  {
    id: 'elapsed',
    header: 'Elapsed',
    // A row that stopped being re-ingested while still open ran *at least* this long, so the figure is
    // marked as the bound it is.
    cell: (row) => `${row.elapsedIsLowerBound ? '≥ ' : ''}${formatDuration(row.elapsedMs)}`,
    sortingField: 'elapsedMs',
  },
  { id: 'budget', header: 'Budget', cell: (row) => duration(row.budgetMs), sortingField: 'budgetMs' },
  OVER_BY_COLUMN,
  {
    id: 'verdict',
    header: 'Verdict',
    cell: (row) => <Badge variant={VERDICT_VARIANTS[row.verdict]}>{VERDICT_LABELS[row.verdict]}</Badge>,
    sortingField: 'verdict',
  },
  {
    id: 'lastSeen',
    header: 'Last seen',
    cell: (row) => timestamp(row.lastSeenAtMs),
    sortingField: 'lastSeenAtMs',
  },
];

interface SlaExceptionTableProps {
  heading: string;
  rows: SlaExceptionRow[];
  /**
   * How many of the tier's rows are exceptions, pre-cap. Not the size of `rows`, which also carries healthy
   * rows for the toggle to reveal.
   */
  exceptionTotal: number;
  /** What `rows` was capped at server-side, which is the other way the list can be short. */
  exceptionCap: number;
  /** Rendered verbatim, and only when the cap actually withheld rows. */
  capNote?: string;
  /** COHORT only: in website scope every row is the one website's, so the column would repeat itself. */
  showWebsite: boolean;
  groupNames: Record<string, string>;
  groupHeader: string;
}

/**
 * Which individual records are overrunning, worst margin first. Sorted, filtered and paged in the browser
 * because the accessor takes one field name with a hardcoded `DESC` and no page parameter — but the backend
 * has already ranked and capped by margin, so what arrives is the worst of the population.
 *
 * The breaching filter starts on: a reader who opens this table is asking what is over budget, and stale and
 * unregistered rows answer a different question one click away.
 */
export const SlaExceptionTable: React.FC<SlaExceptionTableProps> = ({
  heading,
  rows,
  exceptionTotal,
  exceptionCap,
  capNote,
  showWebsite,
  groupNames,
  groupHeader,
}) => {
  const [breachingOnly, setBreachingOnly] = useState(true);
  const [groupKey, setGroupKey] = useState(ALL_GROUPS);

  const columns = useMemo(
    () => columnDefinitions(showWebsite, groupNames, groupHeader),
    [showWebsite, groupNames, groupHeader]
  );

  // Filtered before the hook rather than through its `filteringFunction`, which is built around a text box
  // this table does not have.
  const visible = useMemo(
    () =>
      rows.filter(
        (row) =>
          (!breachingOnly || BREACHING_VERDICTS.includes(row.verdict)) &&
          (groupKey === ALL_GROUPS || row.groupKey === groupKey)
      ),
    [rows, breachingOnly, groupKey]
  );

  const { items, collectionProps, paginationProps, actions } = useCollection(visible, {
    sorting: { defaultState: { sortingColumn: OVER_BY_COLUMN, isDescending: true } },
    pagination: { pageSize: PAGE_SIZE },
  });

  // From the rows on screen rather than the tier's groups: an option that filters to nothing is a dead end,
  // and the cap means a group can be configured, measured and still absent. "On screen" includes the
  // breaching filter — with it on, a group of only stale rows would be offered and land on "No record
  // matches these filters.". Not derived from `visible`, which also filters by `groupKey` and would collapse
  // the list to the group already selected.
  const groupOptions: SelectProps.Option[] = useMemo(() => {
    const candidates = breachingOnly
      ? rows.filter((row) => BREACHING_VERDICTS.includes(row.verdict))
      : rows;
    const keys = Array.from(new Set(candidates.map((row) => row.groupKey)));
    return [
      { value: ALL_GROUPS, label: `All ${groupHeader.toLowerCase()}s` },
      ...keys.map((key) => ({ value: key, label: groupNames[key] ?? key })),
    ];
  }, [rows, breachingOnly, groupNames, groupHeader]);

  // `groupKey` outlives the options it was chosen from: the toggle narrows them, and a window change
  // re-renders in place with new `rows`. Either can retire the selected group, leaving `selectedGroup` on
  // "All …s" while `visible` still filters for a key nothing matches — an empty table under a control
  // claiming it shows everything.
  useEffect(() => {
    if (groupKey !== ALL_GROUPS && !groupOptions.some((option) => option.value === groupKey)) {
      setGroupKey(ALL_GROUPS);
      actions.setCurrentPage(1);
    }
  }, [groupOptions, groupKey, actions]);

  const selectedGroup = groupOptions.find((option) => option.value === groupKey) ?? groupOptions[0];

  return (
    <div className="ovp-sla-exceptions">
      <Table
        {...collectionProps}
        data-testid="sla-exception-table"
        columnDefinitions={columns}
        items={items}
        trackBy="id"
        variant="embedded"
        header={<Header variant="h3" counter={`(${visible.length})`}>{heading}</Header>}
        filter={
          <SpaceBetween direction="horizontal" size="m">
            <Toggle
              checked={breachingOnly}
              onChange={({ detail }) => {
                setBreachingOnly(detail.checked);
                actions.setCurrentPage(1);
              }}
              data-testid="sla-exception-breaching-only"
            >
              Breaching only
            </Toggle>
            <Select
              selectedOption={selectedGroup}
              options={groupOptions}
              onChange={({ detail }) => {
                setGroupKey(detail.selectedOption.value ?? ALL_GROUPS);
                actions.setCurrentPage(1);
              }}
              data-testid="sla-exception-group-filter"
            />
          </SpaceBetween>
        }
        pagination={<Pagination {...paginationProps} />}
        empty="No record matches these filters."
      />
      {/* R12: an uncapped list carries no note, because a note that is always there stops being read. Two ways
          it can be short: more exceptions than were listed, or the list filled the cap. */}
      {capNote && (exceptionTotal > rows.length || rows.length >= exceptionCap) && (
        <p className="ovp-sla-exceptions__cap-note" data-testid="sla-exception-cap-note">
          {capNote}
        </p>
      )}
    </div>
  );
};
